import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  ApiResponse, 
  LoginRequest, 
  LoginResponse, 
  CreateUserRequest,
  User,
  Task,
  CreateTaskRequest,
  UpdateTaskRequest,
  PaginatedResponse,
  PaginationParams,
  API_ENDPOINTS 
} from '../types/api';

class ApiClient {
  private client: AxiosInstance;
  private baseURL: string;

  constructor(baseURL: string = process.env.REACT_APP_API_URL || 'http://localhost:8080') {
    this.baseURL = baseURL;
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized - remove tokens and redirect to login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Helper method to handle API responses
  private async handleResponse<T>(response: AxiosResponse): Promise<T> {
    const apiResponse: ApiResponse<T> = response.data;
    
    if (!apiResponse.success) {
      throw new Error(apiResponse.error || 'API request failed');
    }
    
    return apiResponse.data as T;
  }

  // Auth methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post(API_ENDPOINTS.AUTH.LOGIN, credentials);
    const result = await this.handleResponse<LoginResponse>(response);
    
    // Store tokens
    localStorage.setItem('accessToken', result.accessToken);
    localStorage.setItem('refreshToken', result.refreshToken);
    
    return result;
  }

  async register(userData: CreateUserRequest): Promise<LoginResponse> {
    const response = await this.client.post(API_ENDPOINTS.AUTH.REGISTER, userData);
    const result = await this.handleResponse<LoginResponse>(response);
    
    // Store tokens
    localStorage.setItem('accessToken', result.accessToken);
    localStorage.setItem('refreshToken', result.refreshToken);
    
    return result;
  }

  async logout(): Promise<void> {
    try {
      await this.client.post(API_ENDPOINTS.AUTH.LOGOUT);
    } finally {
      // Always remove tokens even if logout fails
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }

  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await this.client.post(API_ENDPOINTS.AUTH.REFRESH, {
      refreshToken,
    });
    
    const result = await this.handleResponse<{ accessToken: string }>(response);
    localStorage.setItem('accessToken', result.accessToken);
    
    return result.accessToken;
  }

  // User methods
  async getUsers(params?: PaginationParams): Promise<PaginatedResponse<User>> {
    const response = await this.client.get(API_ENDPOINTS.USERS.LIST, { params });
    return this.handleResponse<PaginatedResponse<User>>(response);
  }

  async getUser(id: string): Promise<User> {
    const response = await this.client.get(API_ENDPOINTS.USERS.GET(id));
    return this.handleResponse<User>(response);
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    const response = await this.client.put(API_ENDPOINTS.USERS.UPDATE(id), userData);
    return this.handleResponse<User>(response);
  }

  async deleteUser(id: string): Promise<void> {
    await this.client.delete(API_ENDPOINTS.USERS.DELETE(id));
  }

  // Task methods
  async getTasks(params?: PaginationParams): Promise<PaginatedResponse<Task>> {
    const response = await this.client.get(API_ENDPOINTS.TASKS.LIST, { params });
    return this.handleResponse<PaginatedResponse<Task>>(response);
  }

  async getTask(id: string): Promise<Task> {
    const response = await this.client.get(API_ENDPOINTS.TASKS.GET(id));
    return this.handleResponse<Task>(response);
  }

  async createTask(taskData: CreateTaskRequest): Promise<Task> {
    const response = await this.client.post(API_ENDPOINTS.TASKS.CREATE, taskData);
    return this.handleResponse<Task>(response);
  }

  async updateTask(id: string, taskData: UpdateTaskRequest): Promise<Task> {
    const response = await this.client.put(API_ENDPOINTS.TASKS.UPDATE(id), taskData);
    return this.handleResponse<Task>(response);
  }

  async deleteTask(id: string): Promise<void> {
    await this.client.delete(API_ENDPOINTS.TASKS.DELETE(id));
  }

  // Health check
  async healthCheck(): Promise<any> {
    const response = await this.client.get('/health');
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export { ApiClient }; 